import rawConfig from '../../../client.config.json';

/**
 * Configurazione specifica del cliente.
 *
 * I valori arrivano da `src/client.config.json`, che e' l'unico file da
 * modificare per riusare l'applicazione su un cliente diverso.
 * Nessun componente deve contenere nomi, indirizzi, email o codici azienda
 * scritti a mano: vanno sempre letti da qui.
 */
export interface ClientBrand {
  readonly appName: string;
  readonly shortName: string;
  readonly legalName: string;
  readonly logo: string;
  readonly themeColor: string;
  readonly backgroundColor: string;
}

export interface ClientCompany {
  readonly vatNumber: string;
  readonly registeredOffice: string;
  readonly operationalOffice: string;
  readonly phoneLabel: string;
  readonly phoneHref: string;
  readonly mobileLabel: string;
  readonly mobileHref: string;
  readonly email: string;
  readonly pec: string;
}

export interface ClientApi {
  readonly companyId: string;
  readonly basePath: string;
}

export interface ClientStorage {
  readonly prefix: string;
}

export interface ClientDeploy {
  readonly cloudflareAccountId: string;
  readonly pagesProject: string;
  readonly appHost: string;
  readonly upstreamOrigin: string;
  readonly upstreamPrefix: string;
}

export interface ClientConfig {
  readonly clientId: string;
  readonly brand: ClientBrand;
  readonly company: ClientCompany;
  readonly api: ClientApi;
  readonly storage: ClientStorage;
  readonly deploy: ClientDeploy;
}

export const CLIENT: ClientConfig = rawConfig as unknown as ClientConfig;

/** True quando un valore e' ancora un segnaposto da confermare col cliente. */
export function isPlaceholder(value: string): boolean {
  return typeof value === 'string' && value.startsWith('TODO_');
}

/** Prefissa una chiave di storage locale col namespace del cliente. */
export function storageKey(name: string): string {
  return `${CLIENT.storage.prefix}.${name}`;
}

/**
 * Chiavi di cache locale, tutte derivate dal prefisso del cliente.
 * Centralizzate qui perche' l'interceptor deve poterle ripulire in blocco
 * alla scadenza della sessione: aggiungerne una qui la registra ovunque.
 */
export const STORAGE_KEYS = {
  interventiFlat: storageKey('interventi.flat.v1'),
  interventiGroup: storageKey('interventi.group.v1'),
  sosState: storageKey('sos_state.v1'),
  deviceListState: storageKey('devicelist_state.v1'),
  scadenze: storageKey('scadenze.v1'),
  documenti: storageKey('documenti.v1'),
  proposte: storageKey('proposte'),
} as const;

/** Tutte le chiavi di cache, per la pulizia alla scadenza sessione. */
export const ALL_STORAGE_KEYS: readonly string[] = Object.values(STORAGE_KEYS);

/** Nome del database locale (Ionic Storage). */
export const STORAGE_DB_NAME = `__${CLIENT.storage.prefix}`;
